import { ConfigurationError } from '@cian/peperrors/shared';
import * as React from 'react';

type TOfferHoverSource = 'listing' | 'pin';

interface IOfferHover {
  offerId: number;
  source: TOfferHoverSource;
}

interface IOfferHoverApi {
  setHoveredOfferId(id: number, source?: TOfferHoverSource): void;
  resetHoveredOfferId(): void;
}

// Два контекста: команды (api) отделены от значения, чтобы писатели наведения не ререндерились при смене hoveredOfferId
const OfferHoverStateContext = React.createContext<IOfferHover | null>(null);
const OfferHoverApiContext = React.createContext<IOfferHoverApi | null>(null);
OfferHoverStateContext.displayName = 'OfferHoverStateContext';
OfferHoverApiContext.displayName = 'OfferHoverApiContext';

export const OfferHoverProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [hover, setHover] = React.useState<IOfferHover | null>(null);

  const setHoveredOfferId = React.useCallback((offerId: number, source: TOfferHoverSource = 'listing') => {
    setHover(current => (current?.offerId === offerId && current.source === source ? current : { offerId, source }));
  }, []);

  const resetHoveredOfferId = React.useCallback(() => setHover(null), []);

  const api = React.useMemo<IOfferHoverApi>(
    () => ({ setHoveredOfferId, resetHoveredOfferId }),
    [setHoveredOfferId, resetHoveredOfferId],
  );

  return (
    <OfferHoverApiContext.Provider value={api}>
      <OfferHoverStateContext.Provider value={hover}>{children}</OfferHoverStateContext.Provider>
    </OfferHoverApiContext.Provider>
  );
};

export function useOfferHoverApi(): IOfferHoverApi {
  const context = React.useContext(OfferHoverApiContext);

  if (!context) {
    throw new ConfigurationError({
      message: 'useOfferHoverApi used outside of OfferHoverProvider',
      domain: 'OfferHoverContext',
    });
  }

  return context;
}

export function useHoveredOfferId(): number | null {
  return React.useContext(OfferHoverStateContext)?.offerId ?? null;
}

export function useOfferHoverSource(): TOfferHoverSource | null {
  return React.useContext(OfferHoverStateContext)?.source ?? null;
}
